import Papa from 'papaparse'
import { compact, isEmpty } from 'lodash-es'
import type { RouteResponse } from '@/api/main/types.gen'

export const DEFAULT_ROUTE_PROBLEMS_SCHEMA = 'control_data_next'

export type OppositeDirectionSegment = { segmentId: string; position: number | null }
export type MismatchedNode = { nodeId: string; segmentId: string | null; position: number | null }
export type DisconnectedSegments = { segmentId: string; nextSegmentId: string | null; position: number | null }

export type RouteProblem = {
  routeId: string
  routeVersion: string
  isActive: boolean
  missingSegments: string[]
  missingNodes: string[]
  oppositeDirectionSegments: OppositeDirectionSegment[]
  mismatchedEntranceNodes: MismatchedNode[]
  mismatchedExitNodes: MismatchedNode[]
  disconnectedSegments: DisconnectedSegments[]
}

export const PROBLEM_TYPES = [
  'missingSegments',
  'missingNodes',
  'oppositeDirectionSegments',
  'mismatchedEntranceNodes',
  'mismatchedExitNodes',
  'disconnectedSegments',
] as const

export type ProblemType = (typeof PROBLEM_TYPES)[number]

type CsvRow = Partial<Record<string, string>>

/** The generated client types the CSV endpoint as a Blob, but it is fetched with `parseAs: 'text'`. */
export const isCsvText = (value: unknown): value is string => typeof value === 'string'

const parsePosition = (raw: string | undefined) => (raw === undefined ? null : Number(raw))

const splitIds = (raw: string | undefined) => compact((raw ?? '').split(' '))

const splitEntries = (raw: string | undefined) => compact((raw ?? '').split(';').map(entry => entry.trim()))

// Entry formats come from `RouteProblems.as_csv_row` in backend/city_sense_backend/maps/route_problems.py
const OPPOSITE_DIRECTION_RE = /^(\S+)(?: \(position (\d+)\))?$/
const MISMATCHED_NODE_RE = /^(\S+)(?: \(street segment (\S+), position (\d+)\))?$/
const DISCONNECTED_RE = /^(\S+)(?: -> (\S+))?(?: \(position (\d+)\))?$/

const parseOppositeDirection = (entry: string): OppositeDirectionSegment => {
  const match = OPPOSITE_DIRECTION_RE.exec(entry)
  return match ? { segmentId: match[1], position: parsePosition(match[2]) } : { segmentId: entry, position: null }
}

const parseMismatchedNode = (entry: string): MismatchedNode => {
  const match = MISMATCHED_NODE_RE.exec(entry)
  return match
    ? { nodeId: match[1], segmentId: match[2] ?? null, position: parsePosition(match[3]) }
    : { nodeId: entry, segmentId: null, position: null }
}

const parseDisconnected = (entry: string): DisconnectedSegments => {
  const match = DISCONNECTED_RE.exec(entry)
  return match
    ? { segmentId: match[1], nextSegmentId: match[2] ?? null, position: parsePosition(match[3]) }
    : { segmentId: entry, nextSegmentId: null, position: null }
}

const compareRoutes = (a: RouteProblem, b: RouteProblem) =>
  a.routeId.localeCompare(b.routeId, undefined, { numeric: true }) ||
  a.routeVersion.localeCompare(b.routeVersion, undefined, { numeric: true })

export const parseRouteProblemsCsv = (csv: string): RouteProblem[] =>
  Papa.parse<CsvRow>(csv, { header: true, skipEmptyLines: true })
    .data.filter(row => !isEmpty(row.route_id))
    .map(row => ({
      routeId: row.route_id ?? '',
      routeVersion: row.route_version ?? '',
      isActive: row.is_active !== 'False',
      missingSegments: splitIds(row.missing_segments),
      missingNodes: splitIds(row.missing_nodes),
      oppositeDirectionSegments: splitEntries(row.opposite_direction_segments).map(parseOppositeDirection),
      mismatchedEntranceNodes: splitEntries(row.mismatched_entrance_nodes).map(parseMismatchedNode),
      mismatchedExitNodes: splitEntries(row.mismatched_exit_nodes).map(parseMismatchedNode),
      disconnectedSegments: splitEntries(row.disconnected_segments).map(parseDisconnected),
    }))
    .sort(compareRoutes)

export const problemTypesOf = (problem: RouteProblem): ProblemType[] =>
  PROBLEM_TYPES.filter(type => !isEmpty(problem[type]))

/** Number of routes affected by each problem type. */
export const countRoutesByProblemType = (problems: RouteProblem[]): Record<ProblemType, number> =>
  Object.fromEntries(
    PROBLEM_TYPES.map(type => [type, problems.filter(problem => !isEmpty(problem[type])).length])
  ) as Record<ProblemType, number>

export const routeKey = (routeId: string, routeVersion: string) => `${routeId}@${routeVersion}`

export const indexRoutes = (routes: RouteResponse[]): Map<string, RouteResponse> =>
  new Map(routes.map(route => [routeKey(route.id, route.version), route]))

/**
 * Keep problems having at least one of `types` (all when empty) whose route ID or name contains `search`.
 */
export const filterRouteProblems = (
  problems: RouteProblem[],
  { search, types, routes }: { search: string; types: ProblemType[]; routes: Map<string, RouteResponse> }
): RouteProblem[] => {
  const needle = search.trim().toLowerCase()

  return problems.filter(problem => {
    if (!isEmpty(types) && !types.some(type => !isEmpty(problem[type]))) return false
    if (!needle) return true
    const name = routes.get(routeKey(problem.routeId, problem.routeVersion))?.name ?? ''
    return problem.routeId.toLowerCase().includes(needle) || name.toLowerCase().includes(needle)
  })
}

/** Slice one page of `items`, clamping `page` into the valid range. Pages are one-based. */
export const paginate = <T>(items: T[], page: number, pageSize: number) => {
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize))
  const current = Math.min(Math.max(page, 1), pageCount)
  return { items: items.slice((current - 1) * pageSize, current * pageSize), page: current, pageCount }
}

/** Problem types of a route to show in detail, limited to `selected` unless nothing is selected. */
export const visibleProblemTypes = (problem: RouteProblem, selected: ProblemType[]): ProblemType[] =>
  problemTypesOf(problem).filter(type => isEmpty(selected) || selected.includes(type))
